import { action, computed, makeObservable, observable } from 'mobx';
import type { CreateTarget } from 'actions';
import { Aggregate } from 'edit/Aggregate';
import type { EditableEntity } from 'edit/EditableEntity';
import type { FolderReference } from 'edit/reference/EditFolderReference';
import type { ZoneReference } from 'edit/reference/EditZoneReference';
import type { RecurrenceDefinition } from 'api/recurrence_definitions/endpoints/getRecurrenceDefinition';
import type { SaveRecurrenceDefinition, ScheduleNext } from 'api/recurrence_definitions/endpoints/types';
import { DEFAULT_SCHEDULE_NEXT } from 'api/recurrence_definitions/endpoints/types';
import { formatFolderPath } from 'helpers/folderPaths';
import { EditableRecurrence } from './EditableRecurrence';

const DEFAULT_RESUBMIT_ON_ERROR = true;

export interface EditableRecurrenceDefinitionOptions {
  /** Pre-fill from a loaded record but treat as a new record (POST, cleared name). */
  readonly duplicate?: boolean;
  /** Workspace create pre-target: seeds the zone + folder of a blank create form. */
  readonly createIn?: CreateTarget;
}

/**
 * A recurrence definition open for editing: its own fields plus a one-to-many
 * aggregate of `EditableRecurrence` rules. Built directly from the loaded wire
 * record (or `null` for a blank create form) — there is no intermediate
 * "editable data" type; the model *is* the editable state, and `toSave`
 * projects straight back to the request body.
 *
 * `original` is the pre-edit identity used to choose POST vs PUT and to address
 * the PUT URL; `null` means a not-yet-saved record. It flips to a real identity
 * after the first successful save (via `reinitialize` with the server response).
 * It is written only internally (via `_init`), so it is exposed read-only.
 *
 * `isDirty` is "create is always dirty, otherwise the current `toSave` body
 * differs from the snapshot captured at load/reinitialize" — a structural
 * comparison that covers the definition fields and every recurrence, and
 * correctly reads clean again if the user reverts an edit by hand.
 */
export class EditableRecurrenceDefinition implements EditableEntity<RecurrenceDefinition | null> {
  description: string | null = null;
  folder: FolderReference | null = null;
  name: string | null = null;
  private _original: { zoneName: string; name: string } | null = null;
  private readonly _recurrences = new Aggregate<EditableRecurrenceDefinition, EditableRecurrence>(this, (aggregate) => new EditableRecurrence(aggregate));
  resubmitOnError: boolean = DEFAULT_RESUBMIT_ON_ERROR;
  scheduleNext: ScheduleNext = DEFAULT_SCHEDULE_NEXT;
  private _snapshot = '';
  zone: ZoneReference | null = null;

  constructor(loaded: RecurrenceDefinition | null, options?: EditableRecurrenceDefinitionOptions) {
    makeObservable<EditableRecurrenceDefinition, '_original'>(this, {
      description: observable,
      folder: observable.ref,
      name: observable,
      _original: observable.ref,
      resubmitOnError: observable,
      scheduleNext: observable,
      zone: observable.ref,
      isDirty: computed,
    });
    this._init(loaded, options?.duplicate ?? false, options?.createIn);
  }

  get isCreating(): boolean {
    return this._original === null;
  }

  get isDirty(): boolean {
    return this._original === null || JSON.stringify(this.toSave()) !== this._snapshot;
  }

  /**
   * Pre-edit identity (`null` while creating). The `save` callback reads this
   * to choose create vs update and to build the PUT URL.
   */
  get original(): { zoneName: string; name: string } | null {
    return this._original;
  }

  get recurrences(): readonly EditableRecurrence[] {
    return this._recurrences.children;
  }

  /** Append a fresh, empty recurrence rule and return it. */
  createRecurrence = (): EditableRecurrence => this._recurrences.create();

  /**
   * Reset to the loaded record (or a blank create form when `null`) and
   * recapture the dirty baseline. Called after a successful load or save.
   */
  reinitialize = (loaded: RecurrenceDefinition | null): void => {
    this._init(loaded);
  };

  toSave(): SaveRecurrenceDefinition {
    return {
      name: this.name,
      description: this.description,
      zone_name: this.zone?.name ?? null,
      resubmit_on_error: this.resubmitOnError,
      schedule_next: this.scheduleNext,
      folder_id: this.folder?.folder_id ?? null,
      recurrences: this._recurrences.children.map((r) => r.toSave()),
    };
  }

  // Shared by the constructor and `reinitialize`. Rebuilds the recurrence
  // children in place (same aggregate instance, so reactivity survives) and
  // snapshots the resulting save body for dirty tracking. Wrapped in `action`
  // so the whole rebuild — every field plus the clear/append of the children —
  // lands as one transaction; observers see only the final state, never a
  // half-applied intermediate, and re-render once rather than once per write.
  private readonly _init = action((loaded: RecurrenceDefinition | null, duplicate = false, createIn?: CreateTarget): void => {
    this._recurrences.clear();
    if (loaded !== null) {
      if (loaded.zone_name === null) {
        throw new Error(`Recurrence definition '${loaded.name}' has no zone (orphaned by zone deletion); cannot be edited.`);
      }
      if (duplicate) {
        this._original = null;
        this.name = null;
      } else {
        this._original = { zoneName: loaded.zone_name, name: loaded.name };
        this.name = loaded.name;
      }
      this.zone = { name: loaded.zone_name };
      this.folder =
        loaded.folder_id !== null
          ? {
              folder_id: loaded.folder_id,
              path: formatFolderPath(loaded.folder_path),
            }
          : null;
      this.description = loaded.description;
      this.resubmitOnError = loaded.resubmit_on_error ?? DEFAULT_RESUBMIT_ON_ERROR;
      this.scheduleNext = loaded.schedule_next ?? DEFAULT_SCHEDULE_NEXT;
      for (const wire of loaded.recurrences) {
        this._recurrences.append(new EditableRecurrence(this._recurrences, wire));
      }
    } else {
      this._original = null;
      this.name = null;
      // Seed the zone/folder from the workspace create pre-target when present.
      this.zone = createIn ? { name: createIn.zoneName } : null;
      this.folder = createIn?.folderId != null ? { folder_id: createIn.folderId, path: '' } : null;
      this.description = null;
      this.resubmitOnError = DEFAULT_RESUBMIT_ON_ERROR;
      this.scheduleNext = DEFAULT_SCHEDULE_NEXT;
      this._recurrences.append(new EditableRecurrence(this._recurrences));
    }
    this._snapshot = JSON.stringify(this.toSave());
  });
}
